import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ACTION_FAILED_EVENT } from '../lib/actionFeedback';
import type { ActionFailureOptions } from '../lib/actionFeedback';

/**
 * ActionErrorToast — "Couldn't <action> — try again."
 *
 * UX review H7 (card ESidDNa3mmaP): consequential actions failed silently.
 * This is the visible half: it listens for `pear:action-failed` and shows one
 * bottom toast in the same slot and vocabulary as UpdateToast, with an
 * explicit dismiss. It clears itself after a few seconds so a stale failure
 * never parks over the thread.
 *
 * Card 4FFux4skKvYw: a notice may carry its own sentence, its own dismissal
 * window, and a tappable Retry. A failed send uses all three — it is a
 * transient statement ABOUT a send, not a row in the conversation, so it lives
 * here at the shell and auto-clears in ~4s. Retry re-runs the queued send
 * without the user retyping anything.
 *
 * `role="alert"` so a screen reader announces it as it appears, and portalled
 * to body — the app root carries a fade-in transform, and a transformed
 * ancestor becomes the containing block for `position: fixed` (FktjQ7CT4keo,
 * the same lesson UpdateToast and KudosToast document).
 */
const AUTO_DISMISS_MS = 8000;

interface Notice {
  what: string;
  message?: string;
  ttlMs?: number;
  retry?: { label: string; run: () => void };
}

export function ActionErrorToast() {
  const [notice, setNotice] = useState<Notice | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const clear = () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = null;
    };
    const onFail = (e: Event) => {
      const detail = (e as CustomEvent<ActionFailureOptions & { what?: unknown }>).detail;
      const text = typeof detail?.what === 'string' && detail.what ? detail.what : null;
      if (!text && !detail?.message) return;
      clear();
      setNotice({
        what: text ?? '',
        message: detail?.message,
        ttlMs: detail?.ttlMs,
        retry: detail?.retry,
      });
      // One notice per failure: a second failure re-shows the toast and
      // restarts the clock rather than stacking two.
      const ttl = typeof detail?.ttlMs === 'number' && detail.ttlMs > 0 ? detail.ttlMs : AUTO_DISMISS_MS;
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        setNotice(null);
      }, ttl);
    };
    window.addEventListener(ACTION_FAILED_EVENT, onFail);
    return () => {
      window.removeEventListener(ACTION_FAILED_EVENT, onFail);
      clear();
    };
  }, []);

  if (!notice) return null;

  const dismiss = () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    setNotice(null);
  };

  return createPortal(
    <div className="pear-action-error" role="alert" data-test="action-error">
      <span className="pear-action-error-text">
        {notice.message ?? `Couldn't ${notice.what} — try again.`}
      </span>
      {notice.retry && (
        <button
          type="button"
          className="pear-action-error-retry"
          data-test="action-error-retry"
          onClick={() => {
            dismiss();
            notice.retry?.run();
          }}
        >
          {notice.retry.label}
        </button>
      )}
      <button
        type="button"
        className="pear-action-error-dismiss"
        aria-label="Dismiss error notice"
        onClick={dismiss}
      >
        ✕
      </button>
    </div>,
    document.body,
  );
}
